import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt,
  faPlus,
  faTrash,
  faSpinner,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import blueTeamService from '../../services/blueTeamService';
import projectService from '../../services/projectService';
import mitreService from '../../services/mitreService';
import { useAuth } from '../../contexts/AuthContext';

const BlueTeamFindingForm = ({ finding = null, isEditing = false }) => {
  const { projectId, findingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [projects, setProjects] = useState([]);
  const [mitreTechniques, setMitreTechniques] = useState([]);

  const getLocalDateTime = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    project: projectId || '',
    title: '',
    description: '',
    detectionTimestamp: getLocalDateTime(),
    activityTimestamp: '',
    detectionSource: 'siem',
    detectionTool: '',
    severity: 'medium',
    status: 'new',
    suspectedTechnique: null,
    affectedAssets: [],
    iocs: [],
    alertInfo: {
      alertId: '',
      ruleName: '',
      ruleId: '',
      confidence: ''
    },
    analysisNotes: '',
    analyst: '',
    tags: []
  });

  const [newAsset, setNewAsset] = useState({ type: 'ip', value: '', description: '' });
  const [newIoc, setNewIoc] = useState({ type: 'ip', value: '', description: '' });
  const [newTag, setNewTag] = useState('');

  const [initialLoading, setInitialLoading] = useState(isEditing);

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch finding data when editing by ID
  useEffect(() => {
    if (isEditing && findingId && !finding) {
      setInitialLoading(true);
      blueTeamService.getFinding(findingId)
        .then(data => {
          const f = data;
          setFormData({
            project: f.project || projectId || '',
            title: f.title || '',
            description: f.description || '',
            detectionTimestamp: f.detectionTimestamp
              ? getLocalDateTime(new Date(f.detectionTimestamp))
              : getLocalDateTime(),
            activityTimestamp: f.activityTimestamp
              ? getLocalDateTime(new Date(f.activityTimestamp))
              : '',
            detectionSource: f.detectionSource || 'siem',
            detectionTool: f.detectionTool || '',
            severity: f.severity || 'medium',
            status: f.status || 'new',
            suspectedTechnique: f.suspectedTechnique || null,
            affectedAssets: f.affectedAssets || [],
            iocs: f.iocs || [],
            alertInfo: f.alertInfo || { alertId: '', ruleName: '', ruleId: '', confidence: '' },
            analysisNotes: f.analysisNotes || '',
            analyst: f.analyst || '',
            tags: f.tags || []
          });
        })
        .catch(err => {
          console.error('Error fetching finding:', err);
          setError('Failed to load finding data');
        })
        .finally(() => setInitialLoading(false));
    }
  }, [isEditing, findingId, finding]);

  // Auto-fill analyst name from logged-in user when creating new finding
  useEffect(() => {
    if (!isEditing && user && !formData.analyst) {
      setFormData(prev => ({ ...prev, analyst: user.displayName || user.username || '' }));
    }
  }, [user, isEditing]);

  useEffect(() => {
    if (isEditing && finding) {
      setFormData({
        ...finding,
        detectionTimestamp: finding.detectionTimestamp
          ? getLocalDateTime(new Date(finding.detectionTimestamp))
          : getLocalDateTime(),
        activityTimestamp: finding.activityTimestamp
          ? getLocalDateTime(new Date(finding.activityTimestamp))
          : ''
      });
    }
  }, [isEditing, finding]);

  const fetchInitialData = async () => {
    try {
      const [projectsData, techniquesData] = await Promise.all([
        projectService.getProjects(),
        mitreService.getTechniques()
      ]);
      setProjects(projectsData);
      setMitreTechniques(techniquesData);

      // Set default project if provided
      if (projectId && !formData.project) {
        setFormData(prev => ({ ...prev, project: projectId }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAlertInfoChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      alertInfo: {
        ...prev.alertInfo,
        [name]: value
      }
    }));
  };

  const handleTechniqueChange = (e) => {
    const techniqueId = e.target.value;
    if (techniqueId) {
      const technique = mitreTechniques.find(t => t.id === techniqueId);
      setFormData(prev => ({
        ...prev,
        suspectedTechnique: {
          id: technique.id,
          name: technique.name,
          tactic: technique.tactic
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        suspectedTechnique: null
      }));
    }
  };

  const addAsset = () => {
    if (newAsset.value) {
      setFormData(prev => ({
        ...prev,
        affectedAssets: [...prev.affectedAssets, { ...newAsset }]
      }));
      setNewAsset({ type: 'ip', value: '', description: '' });
    }
  };

  const removeAsset = (index) => {
    setFormData(prev => ({
      ...prev,
      affectedAssets: prev.affectedAssets.filter((_, i) => i !== index)
    }));
  };

  const addIoc = () => {
    if (newIoc.value) {
      setFormData(prev => ({
        ...prev,
        iocs: [...prev.iocs, { ...newIoc }]
      }));
      setNewIoc({ type: 'ip', value: '', description: '' });
    }
  };

  const removeIoc = (index) => {
    setFormData(prev => ({
      ...prev,
      iocs: prev.iocs.filter((_, i) => i !== index)
    }));
  };

  const addTag = () => {
    if (newTag && !formData.tags.includes(newTag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const findingData = {
        ...formData,
        detectionTimestamp: new Date(formData.detectionTimestamp).toISOString(),
        activityTimestamp: formData.activityTimestamp
          ? new Date(formData.activityTimestamp).toISOString()
          : undefined
      };

      if (isEditing && findingId) {
        await blueTeamService.updateFinding(findingId, findingData);
        alert('Finding updated successfully!');
      } else {
        await blueTeamService.createFinding(findingData);
        alert('Finding created successfully!');
      }

      // Navigate back to the project's blue team page
      const targetProjectId = projectId || formData.project;
      navigate(`/projects/${targetProjectId}/blue-team`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save finding');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="max-w-4xl mx-auto flex items-center justify-center py-20">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-600 text-3xl" />
        <span className="ml-3 text-gray-600 text-lg">Loading finding data...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-600 hover:text-gray-800 flex items-center"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back
        </button>
      </div>

      <h1 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
        <FontAwesomeIcon icon={faShieldAlt} className="mr-3 text-blue-600" />
        {isEditing ? 'Edit Detection Finding' : 'Log New Detection'}
      </h1>

      <div className="bg-white rounded-lg shadow-md p-6">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Project Selection - only show if not provided in URL */}
          {!projectId ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project *
              </label>
              <select
                name="project"
                value={formData.project}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a project</option>
                {projects.map(project => (
                  <option key={project._id} value={project._id}>
                    {project.name} - {project.client}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <input type="hidden" name="project" value={projectId} />
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              placeholder="Brief description of the detection"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              placeholder="Detailed description of what was detected..."
              required
            />
          </div>

          {/* Timestamps */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Detection Time *
              </label>
              <input
                type="datetime-local"
                name="detectionTimestamp"
                value={formData.detectionTimestamp}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Activity Time (if known)
              </label>
              <input
                type="datetime-local"
                name="activityTimestamp"
                value={formData.activityTimestamp}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Detection Source & Tool */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Detection Source *
              </label>
              <select
                name="detectionSource"
                value={formData.detectionSource}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                required
              >
                {blueTeamService.DETECTION_SOURCES.map(source => (
                  <option key={source.value} value={source.value}>
                    {source.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Detection Tool
              </label>
              <input
                type="text"
                name="detectionTool"
                value={formData.detectionTool}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Splunk, CrowdStrike, etc."
              />
            </div>
          </div>

          {/* Severity & Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Severity *
              </label>
              <select
                name="severity"
                value={formData.severity}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                required
              >
                {blueTeamService.SEVERITY_LEVELS.map(level => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status *
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                required
              >
                {blueTeamService.STATUS_OPTIONS.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Suspected MITRE Technique */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Suspected MITRE Technique
            </label>
            <select
              value={formData.suspectedTechnique?.id || ''}
              onChange={handleTechniqueChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a technique...</option>
              {mitreTechniques.map(technique => (
                <option key={technique.id} value={technique.id}>
                  {technique.id} - {technique.name}
                </option>
              ))}
            </select>
          </div>

          {/* Alert Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-3">Alert Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Alert ID</label>
                <input
                  type="text"
                  name="alertId"
                  value={formData.alertInfo.alertId}
                  onChange={handleAlertInfoChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name</label>
                <input
                  type="text"
                  name="ruleName"
                  value={formData.alertInfo.ruleName}
                  onChange={handleAlertInfoChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rule ID</label>
                <input
                  type="text"
                  name="ruleId"
                  value={formData.alertInfo.ruleId}
                  onChange={handleAlertInfoChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confidence (0-100)</label>
                <input
                  type="number"
                  name="confidence"
                  value={formData.alertInfo.confidence}
                  onChange={handleAlertInfoChange}
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Affected Assets */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-3">Affected Assets</h3>
            <div className="flex gap-2 mb-3">
              <select
                value={newAsset.type}
                onChange={(e) => setNewAsset({ ...newAsset, type: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="ip">IP Address</option>
                <option value="hostname">Hostname</option>
                <option value="user">User</option>
                <option value="service">Service</option>
                <option value="application">Application</option>
                <option value="other">Other</option>
              </select>
              <input
                type="text"
                value={newAsset.value}
                onChange={(e) => setNewAsset({ ...newAsset, value: e.target.value })}
                placeholder="Value"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
              />
              <input
                type="text"
                value={newAsset.description}
                onChange={(e) => setNewAsset({ ...newAsset, description: e.target.value })}
                placeholder="Description (optional)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
              />
              <button
                type="button"
                onClick={addAsset}
                className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>
            {formData.affectedAssets.length > 0 && (
              <div className="space-y-2">
                {formData.affectedAssets.map((asset, index) => (
                  <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                    <span>
                      <span className="font-medium text-blue-600">[{asset.type}]</span> {asset.value}
                      {asset.description && <span className="text-gray-500 ml-2">- {asset.description}</span>}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAsset(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* IOCs */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-3">Indicators of Compromise (IOCs)</h3>
            <div className="flex gap-2 mb-3">
              <select
                value={newIoc.type}
                onChange={(e) => setNewIoc({ ...newIoc, type: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-md"
              >
                {blueTeamService.IOC_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              <input
                type="text"
                value={newIoc.value}
                onChange={(e) => setNewIoc({ ...newIoc, value: e.target.value })}
                placeholder="Value"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
              />
              <input
                type="text"
                value={newIoc.description}
                onChange={(e) => setNewIoc({ ...newIoc, description: e.target.value })}
                placeholder="Description (optional)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
              />
              <button
                type="button"
                onClick={addIoc}
                className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>
            {formData.iocs.length > 0 && (
              <div className="space-y-2">
                {formData.iocs.map((ioc, index) => (
                  <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                    <span>
                      <span className="font-medium text-orange-600">[{ioc.type}]</span> {ioc.value}
                      {ioc.description && <span className="text-gray-500 ml-2">- {ioc.description}</span>}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeIoc(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Analysis Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Analysis Notes
            </label>
            <textarea
              name="analysisNotes"
              value={formData.analysisNotes}
              onChange={handleChange}
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              placeholder="Additional analysis notes..."
            />
          </div>

          {/* Analyst */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Analyst Name *
            </label>
            <input
              type="text"
              name="analyst"
              value={formData.analyst}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              placeholder="Your name"
              required
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Add a tag..."
              />
              <button
                type="button"
                onClick={addTag}
                className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map(tag => (
                  <span
                    key={tag}
                    className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-sm flex items-center"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-1 text-gray-500 hover:text-red-600"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                isEditing ? 'Update Finding' : 'Log Finding'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlueTeamFindingForm;
